export const MAX_URL_CHARS = 8_192;

export type UrlFlag = {
	id: string;
	title: string;
	detail: string;
	falsePositive: string;
};

export type UrlAnalysis = {
	ok: boolean;
	verdict: 'indicators' | 'none' | 'unable';
	message: string;
	inferredScheme: boolean;
	parts: {
		href: string;
		scheme: string;
		userinfo: string;
		host: string;
		hostUnicode: string;
		port: string;
		path: string;
		query: string;
		hash: string;
		etld1: string;
	} | null;
	queryParams: Array<{ key: string; value: string }>;
	flags: UrlFlag[];
	report: string;
};

const SHORTENERS = new Set([
	'bit.ly',
	't.co',
	'goo.gl',
	'tinyurl.com',
	'ow.ly',
	'is.gd',
	'cutt.ly',
	'rebrand.ly',
]);

const MULTI_TLD = new Set([
	'co.uk',
	'org.uk',
	'ac.uk',
	'com.au',
	'net.au',
	'co.in',
	'com.br',
	'co.jp',
	'com.sg',
]);

function bestEffortEtld1(host: string): string {
	const h = host.toLowerCase().replace(/\.$/, '');
	if (!h || h.includes(':') || /^\d+\.\d+\.\d+\.\d+$/.test(h)) {
		return h;
	}
	const labels = h.split('.');
	if (labels.length < 2) {
		return h;
	}
	const last2 = labels.slice(-2).join('.');
	if (MULTI_TLD.has(last2) && labels.length >= 3) {
		return labels.slice(-3).join('.');
	}
	return last2;
}

function hasLookalikes(host: string): boolean {
	// Cyrillic/greek lookalike ranges and mixed scripts with Latin.
	const nonAscii = /[^\u0000-\u007f]/.test(host);
	const latin = /[a-z]/i.test(host);
	const cyrillic = /[\u0400-\u04FF]/.test(host);
	const greek = /[\u0370-\u03FF]/.test(host);
	return nonAscii && ((latin && (cyrillic || greek)) || cyrillic || greek);
}

function countEncodings(s: string): number {
	return (s.match(/%[0-9a-fA-F]{2}/g) || []).length;
}

export function analyzeUrl(input: string): UrlAnalysis {
	const trimmed = input.trim();
	if (!trimmed) {
		return {
			ok: false,
			verdict: 'unable',
			message: 'Paste a URL to analyze.',
			inferredScheme: false,
			parts: null,
			queryParams: [],
			flags: [],
			report: '',
		};
	}
	if (trimmed.length > MAX_URL_CHARS) {
		return {
			ok: false,
			verdict: 'unable',
			message: `URL exceeds the ${MAX_URL_CHARS.toLocaleString()} character limit.`,
			inferredScheme: false,
			parts: null,
			queryParams: [],
			flags: [],
			report: '',
		};
	}

	let candidate = trimmed.replace(/[\u0000-\u001F\u007F]/g, '');
	let inferredScheme = false;
	if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(candidate)) {
		candidate = `https://${candidate}`;
		inferredScheme = true;
	}

	let url: URL;
	try {
		url = new URL(candidate);
	} catch {
		return {
			ok: false,
			verdict: 'unable',
			message: 'Unable to analyze — the input is not a parseable URL.',
			inferredScheme,
			parts: null,
			queryParams: [],
			flags: [],
			report: '',
		};
	}

	const flags: UrlFlag[] = [];
	const userinfo = url.username ? `${url.username}${url.password ? ':' + url.password : ''}` : '';
	if (userinfo || /@/.test(trimmed.split(/[?#]/)[0] || '')) {
		flags.push({
			id: 'userinfo',
			title: 'Userinfo / @ confusion',
			detail: 'Credentials or an @ before the host can make the real host less obvious.',
			falsePositive: 'Some legitimate legacy URLs still use userinfo.',
		});
	}

	const host = url.hostname;
	let hostUnicode = host;
	try {
		hostUnicode = host.startsWith('xn--') ? punycodeToUnicode(host) : host;
	} catch {
		hostUnicode = host;
	}

	if (host.startsWith('xn--') || hostUnicode !== host) {
		flags.push({
			id: 'punycode',
			title: 'Punycode / IDN host',
			detail: `Host uses internationalized domain encoding (${host} → ${hostUnicode}).`,
			falsePositive: 'Many legitimate non-Latin domains use punycode.',
		});
	}
	if (hasLookalikes(hostUnicode)) {
		flags.push({
			id: 'lookalike',
			title: 'Lookalike / mixed-script characters',
			detail: 'Host contains non-ASCII characters that can resemble Latin letters.',
			falsePositive: 'Genuine IDN brands may use native scripts.',
		});
	}
	if (/^(\d{1,3}\.){3}\d{1,3}$/.test(host) || host.includes(':')) {
		flags.push({
			id: 'ip-host',
			title: 'IP-literal host',
			detail: 'The host is an IP address rather than a domain name.',
			falsePositive: 'Internal tools and some CDNs use IP literals.',
		});
	}
	if (url.port && url.port !== '80' && url.port !== '443') {
		flags.push({
			id: 'unusual-port',
			title: 'Unusual port',
			detail: `Explicit port ${url.port} is outside the common 80/443 pair.`,
			falsePositive: 'Dev servers and alternate HTTPS ports are common.',
		});
	}

	const encodedCount = countEncodings(url.pathname + url.search + url.hash);
	if (encodedCount >= 3) {
		flags.push({
			id: 'encoding',
			title: 'Excessive percent-encoding',
			detail: `Found ${encodedCount} percent-encoded sequences in path/query/fragment.`,
			falsePositive: 'International paths and tracking parameters encode often.',
		});
	}

	const queryParams: Array<{ key: string; value: string }> = [];
	url.searchParams.forEach((value, key) => {
		queryParams.push({ key, value });
		if (/^https?:\/\//i.test(value) || /%3A%2F%2F/i.test(value)) {
			flags.push({
				id: 'nested-url',
				title: 'Nested URL in query',
				detail: `Parameter “${key}” appears to embed another URL.`,
				falsePositive: 'OAuth redirects and return_to parameters often do this.',
			});
		}
	});

	const etld1 = bestEffortEtld1(host);
	if (SHORTENERS.has(etld1) || SHORTENERS.has(host.toLowerCase())) {
		flags.push({
			id: 'shortener',
			title: 'Known shortener domain',
			detail: `Host matches a common shortener (${etld1}). Destination is hidden until resolved — this tool does not fetch it.`,
			falsePositive: 'Marketing and document sharing use shorteners legitimately.',
		});
	}

	if (/<script|javascript:/i.test(trimmed)) {
		flags.push({
			id: 'scriptish',
			title: 'Script-looking fragment',
			detail: 'Input contained script-like text; it is displayed as plain text only and is not executed.',
			falsePositive: 'Rare in real URLs; usually a test or copy/paste artifact.',
		});
	}

	const uniqueFlags = [...new Map(flags.map((f) => [f.id, f])).values()];
	const verdict = uniqueFlags.length ? 'indicators' : 'none';
	const message =
		verdict === 'indicators'
			? 'Indicators found'
			: 'No indicators from these checks';

	const parts = {
		href: url.href,
		scheme: url.protocol.replace(':', ''),
		userinfo,
		host,
		hostUnicode,
		port: url.port || (url.protocol === 'https:' ? '443' : url.protocol === 'http:' ? '80' : ''),
		path: url.pathname,
		query: url.search,
		hash: url.hash,
		etld1,
	};

	const report = [
		'KB Suspicious URL Analyzer — findings (not a verdict of safe/malicious)',
		`Verdict: ${message}`,
		inferredScheme ? 'Note: https:// was inferred for parsing.' : '',
		`Scheme: ${parts.scheme}`,
		`Host: ${parts.host}`,
		`Unicode host: ${parts.hostUnicode}`,
		`eTLD+1 (best effort): ${parts.etld1}`,
		`Port: ${parts.port}`,
		`Path: ${parts.path}`,
		`Query: ${parts.query || '—'}`,
		`Fragment: ${parts.hash || '—'}`,
		'Flags:',
		...uniqueFlags.map((f) => `- ${f.title}: ${f.detail}`),
	]
		.filter(Boolean)
		.join('\n');

	return {
		ok: true,
		verdict,
		message,
		inferredScheme,
		parts,
		queryParams,
		flags: uniqueFlags,
		report,
	};
}

/** Minimal punycode decoder for display (UTS #46 / RFC 3492 subset). */
function punycodeToUnicode(ascii: string): string {
	try {
		const u = new URL(`http://${ascii}/`);
		const dt = (URL as unknown as { domainToUnicode?: (s: string) => string }).domainToUnicode;
		if (typeof dt === 'function') {
			return dt(ascii);
		}
		return u.hostname;
	} catch {
		return ascii;
	}
}
